// ===========================================
// COMPONENTE: ServiciosMultiSelector
// ===========================================
// Multi-select de servicios con tabla de edición inline

import * as React from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import type { Servicio } from '@/types/database'
import type { ServicioConPrecio } from '@/components/clientes/wizard/clienteSchema'
import { NOMBRES_MESES } from '@/lib/billing-utils'

interface ServiciosMultiSelectorProps {
  servicios: Servicio[]
  value: ServicioConPrecio[]
  onChange: (servicios: ServicioConPrecio[]) => void
  disabled?: boolean
}

const FRECUENCIAS = [
  { value: 'mensual', label: 'Mensual' },
  { value: 'bimestral', label: 'Bimestral' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
] as const

export function ServiciosMultiSelector({
  servicios,
  value = [],
  onChange,
  disabled = false,
}: ServiciosMultiSelectorProps) {
  const [open, setOpen] = React.useState(false)
  const [busqueda, setBusqueda] = React.useState('')
  const scrollRef = React.useRef<HTMLDivElement>(null)

  // Handler manual para wheel events (fix para Radix Popover)
  const handleWheel = React.useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    const el = scrollRef.current
    if (!el) return

    e.stopPropagation()
    el.scrollTop += e.deltaY
  }, [])

  // IDs seleccionados para lookup rápido
  const seleccionados = React.useMemo(
    () => new Set(value.map((v) => v.servicio_id)),
    [value]
  )

  // Filtrar catálogo
  const serviciosFiltrados = React.useMemo(() => {
    if (!busqueda) return servicios
    const query = busqueda.toLowerCase()
    return servicios.filter((s) => s.nombre.toLowerCase().includes(query))
  }, [servicios, busqueda])

  // Toggle servicio en multiselect
  const handleToggle = (servicio: Servicio) => {
    if (seleccionados.has(servicio.id)) {
      // Quitar
      onChange(value.filter((v) => v.servicio_id !== servicio.id))
    } else {
      // Agregar con auto-fill de precio
      const frecuenciaDefault =
        servicio.periodicidad && servicio.periodicidad !== 'unico'
          ? (servicio.periodicidad as ServicioConPrecio['frecuencia'])
          : 'mensual'
      onChange([
        ...value,
        {
          servicio_id: servicio.id,
          precio: servicio.precio_estimado || 0,
          frecuencia: frecuenciaDefault,
          mes_inicio: 1,
        },
      ])
    }
  }

  // Actualizar frecuencia
  const actualizarFrecuencia = (
    index: number,
    frecuencia: ServicioConPrecio['frecuencia']
  ) => {
    const nuevo = [...value]
    nuevo[index] = { ...nuevo[index], frecuencia }
    onChange(nuevo)
  }

  // Actualizar mes de inicio
  const actualizarMesInicio = (index: number, mes: number) => {
    const nuevo = [...value]
    nuevo[index] = { ...nuevo[index], mes_inicio: mes }
    onChange(nuevo)
  }

  // Actualizar precio
  const actualizarPrecio = (index: number, precioStr: string) => {
    const precio = parseFloat(precioStr) || 0
    const nuevo = [...value]
    nuevo[index] = { ...nuevo[index], precio }
    onChange(nuevo)
  }

  // Eliminar servicio
  const eliminarServicio = (servicioId: string) => {
    onChange(value.filter((v) => v.servicio_id !== servicioId))
  }

  // Obtener datos completos de un servicio
  const obtenerDatos = (servicioId: string) => {
    return servicios.find((s) => s.id === servicioId)
  }

  return (
    <div className="space-y-4">
      {/* Selector Multi-Select */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className="w-full justify-between font-normal h-auto min-h-10 px-3 py-2"
          >
            <div className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              <span>
                Seleccionar servicios... ({value.length} seleccionado
                {value.length !== 1 ? 's' : ''})
              </span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          {/* Búsqueda */}
          <div className="p-2 border-b">
            <Input
              placeholder="Buscar servicio..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="h-8"
            />
          </div>

          {/* Lista con checkboxes */}
          <div
            ref={scrollRef}
            onWheel={handleWheel}
            className="max-h-72 overflow-y-auto overscroll-contain"
          >
            <div className="p-2 space-y-1">
              {serviciosFiltrados.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground">
                    No se encontraron servicios
                  </p>
                </div>
              ) : (
                serviciosFiltrados.map((servicio) => (
                  <label
                    key={servicio.id}
                    className={cn(
                      'flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer',
                      'hover:bg-muted transition-colors',
                      seleccionados.has(servicio.id) && 'bg-muted'
                    )}
                  >
                    <Checkbox
                      checked={seleccionados.has(servicio.id)}
                      onCheckedChange={() => handleToggle(servicio)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {servicio.nombre}
                      </div>
                      {servicio.precio_estimado && (
                        <div className="text-xs text-muted-foreground">
                          ${servicio.precio_estimado.toLocaleString('es-AR')}
                        </div>
                      )}
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Tabla de Servicios Seleccionados */}
      {value.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Servicio</TableHead>
                <TableHead className="w-[180px]">Frecuencia</TableHead>
                <TableHead className="w-[140px]">Inicio</TableHead>
                <TableHead className="w-[150px]">Precio</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {value.map((item, index) => {
                const servicio = obtenerDatos(item.servicio_id)

                // Si el servicio no existe en el catálogo (desactivado o eliminado),
                // mostrar una fila de fallback que permite eliminarlo
                if (!servicio) {
                  return (
                    <TableRow key={item.servicio_id} className="bg-muted/30">
                      <TableCell className="font-medium text-muted-foreground">
                        Servicio no disponible
                        <p className="text-xs text-muted-foreground">
                          Este servicio fue desactivado o eliminado
                        </p>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {item.frecuencia}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {item.frecuencia !== 'mensual' ? NOMBRES_MESES[(item.mes_inicio ?? 1) - 1] : '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        ${item.precio.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => eliminarServicio(item.servicio_id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                }

                return (
                  <TableRow key={item.servicio_id}>
                    <TableCell className="font-medium">
                      {servicio.nombre}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={item.frecuencia}
                        onValueChange={(v) =>
                          actualizarFrecuencia(
                            index,
                            v as ServicioConPrecio['frecuencia']
                          )
                        }
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FRECUENCIAS.map((f) => (
                            <SelectItem key={f.value} value={f.value}>
                              {f.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {item.frecuencia !== 'mensual' ? (
                        <Select
                          value={String(item.mes_inicio ?? 1)}
                          onValueChange={(v) => actualizarMesInicio(index, parseInt(v, 10))}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {NOMBRES_MESES.map((mes, i) => (
                              <SelectItem key={i + 1} value={String(i + 1)}>
                                {mes}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-sm text-muted-foreground px-3">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={item.precio}
                        onChange={(e) =>
                          actualizarPrecio(index, e.target.value)
                        }
                        className="h-9"
                        min="0"
                        step="0.01"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => eliminarServicio(item.servicio_id)}
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
